import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';
import { leaveCalculationService } from '../../services/leaveCalculationService';
import { FiCalendar, FiClock, FiRefreshCw, FiPlus, FiUser, FiActivity, FiMapPin, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

const LeaveOverview = () => {
  const { currentUser, userProfile } = useAuth();
  const { currentTenant } = useTenant();
  const [leaveData, setLeaveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user ID from either currentUser.uid or userProfile.uid
  const userId = currentUser?.uid || userProfile?.uid;
  const tenantId = currentTenant?.id;

  useEffect(() => {
    loadLeaveData();
  }, [userId, tenantId]);

  const loadLeaveData = async () => {
    if (!userId || !tenantId) {
      console.log('LeaveOverview: Missing data', { userId, tenantId });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('LeaveOverview: Loading data for user:', userId);
      const data = await leaveCalculationService.getLeaveOverview(userId, tenantId);
      setLeaveData(data);
    } catch (err) {
      console.error('Error loading leave data:', err);
      setError('Failed to load leave information');
      toast.error('Failed to load leave information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLeave = () => {
    // Navigate to calendar with leave request parameters
    toast.success('Opening calendar to submit leave request...');
    
    // Close any open modals first
    const closeEvent = new CustomEvent('closeUserProfile');
    window.dispatchEvent(closeEvent);
    
    // Navigate to calendar with leave type pre-selected
    setTimeout(() => {
      window.location.href = '/calendar?action=add&type=leave';
    }, 100);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-lg border p-6">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
      </div>
    );
  }

  if (error || !leaveData) {
    return (
      <div className="text-center py-12">
        <FiCalendar className="mx-auto w-12 h-12 text-gray-400 mb-4" />
        <p className="text-gray-600 mb-4">{error || 'No leave data available'}</p>
        <button
          onClick={loadLeaveData}
          className="flex items-center space-x-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiRefreshCw className="w-4 h-4" />
          <span>Retry</span>
        </button>
      </div>
    );
  }

  const used = leaveData.usedDays.leave || 0;
  const allowance = leaveData.allowances.leave || 0;
  const remaining = allowance - used;
  const sickUsed = leaveData.usedDays.sick || 0;

  return (
    <div className="space-y-6">
      {/* Leave Tiles - Similar to Admin View */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Annual Leave */}
        <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-blue-500">
          <div className="flex items-center mb-2">
            <FiCalendar className="w-5 h-5 text-blue-600 mr-2" />
            <span className="text-blue-600 font-medium">Annual Leave</span>
          </div>
          <div className="text-2xl font-bold text-blue-900 mb-1">
            {remaining} days left
          </div>
          <div className="text-sm text-blue-700">
            {used} / {allowance} used
          </div>
        </div>

        {/* Sick Leave */}
        <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
          <div className="flex items-center mb-2">
            <FiUser className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-600 font-medium">Sick Leave</span>
          </div>
          <div className="text-2xl font-bold text-red-900 mb-1">
            {sickUsed} days taken
          </div>
          <div className="text-sm text-red-700">
            This year
          </div>
        </div>

        {/* Submit Leave */}
        <div 
          className="bg-green-50 rounded-lg p-6 border-l-4 border-green-500 cursor-pointer hover:bg-green-100 transition-colors"
          onClick={handleSubmitLeave}
        >
          <div className="flex items-center mb-2">
            <FiPlus className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-green-600 font-medium">Submit Leave</span>
          </div>
          <div className="text-2xl font-bold text-green-900 mb-1">
            Request Leave
          </div>
          <div className="text-sm text-green-700">
            Click to submit
          </div>
        </div>
      </div>

      {/* Leave Year Info */}
      <div className="text-sm text-gray-600">
        Leave Year: {formatDate(leaveData.leaveYear.start)} - {formatDate(leaveData.leaveYear.end)}
      </div>
    </div>
  );
};

export default LeaveOverview;
